// src/components/dashboard/StatusFunnel.js
import React, { useMemo } from 'react';

/**
 * Status Funnel Visualization
 * Shows the progression of bookings through different statuses
 *
 * @param {Object} data - Status counts { pending, confirmed, vehicleReady, checkedIn, completed, canceled, noShow }
 */
const StatusFunnel = ({ data }) => {
  // Calculate total for active flow
  const total = useMemo(() => {
    return data.pending + data.confirmed + data.vehicleReady + data.checkedIn + data.completed;
  }, [data]);

  // Funnel stages (main flow) - with light/dark mode support
  const stages = useMemo(() => [
    {
      label: 'Pending',
      count: data.pending,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      bgLight: 'bg-yellow-100 dark:bg-yellow-500/20'
    },
    {
      label: 'Confirmed',
      count: data.confirmed,
      color: 'bg-green-500',
      textColor: 'text-green-600 dark:text-green-400',
      bgLight: 'bg-green-100 dark:bg-green-500/20'
    },
    {
      label: 'Vehicle Ready',
      count: data.vehicleReady,
      color: 'bg-cyan-500',
      textColor: 'text-cyan-600 dark:text-cyan-400',
      bgLight: 'bg-cyan-100 dark:bg-cyan-500/20'
    },
    {
      label: 'Checked In',
      count: data.checkedIn,
      color: 'bg-purple-500',
      textColor: 'text-purple-600 dark:text-purple-400',
      bgLight: 'bg-purple-100 dark:bg-purple-500/20'
    },
    {
      label: 'Completed',
      count: data.completed,
      color: 'bg-blue-500',
      textColor: 'text-blue-600 dark:text-blue-400',
      bgLight: 'bg-blue-100 dark:bg-blue-500/20'
    }
  ], [data]);

  // Calculate width percentage for each stage
  const getWidth = (count) => {
    if (total === 0) return 0;
    const percentage = (count / total) * 100;
    return Math.max(percentage, 5); // Minimum 5% width for visibility
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4 lg:p-6">
      <div className="mb-6">
        <h3 className="text-base lg:text-lg font-semibold text-app-text-light dark:text-app-text">
          Booking Status Funnel
        </h3>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Active bookings flow through stages
        </p>
      </div>

      {/* Main funnel - vertical on mobile, horizontal on desktop */}
      <div className="space-y-3 mb-6">
        {stages.map((stage, index) => {
          const widthPercent = getWidth(stage.count);
          const conversionRate = index > 0 && stages[index - 1].count > 0
            ? ((stage.count / stages[index - 1].count) * 100).toFixed(1)
            : null;

          return (
            <div key={stage.label} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-app-text-light dark:text-app-text font-medium">{stage.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${stage.textColor}`}>
                    {stage.count}
                  </span>
                  {conversionRate && (
                    <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                      ({conversionRate}% conversion)
                    </span>
                  )}
                </div>
              </div>
              <div className="relative h-10 bg-gray-200 dark:bg-gray-800/50 rounded-lg overflow-hidden">
                <div
                  className={`h-full ${stage.bgLight} border-l-4 ${stage.color} transition-all duration-500 flex items-center px-3`}
                  style={{ width: `${widthPercent}%` }}
                >
                  <span className={`text-xs font-semibold ${stage.textColor}`}>
                    {((stage.count / total) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Canceled and No Show - shown separately */}
      <div className="pt-4 border-t border-app-border-light dark:border-app-border">
        <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-3 font-medium">
          Outside Main Flow
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-red-100 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-app-text-light dark:text-app-text">Canceled</span>
              <span className="text-lg font-bold text-red-600 dark:text-red-400">{data.canceled}</span>
            </div>
            <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mt-1">
              Cancellation rate: {total > 0 ? ((data.canceled / (total + data.canceled + data.noShow)) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="bg-orange-100 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-app-text-light dark:text-app-text">No Show</span>
              <span className="text-lg font-bold text-orange-600 dark:text-orange-400">{data.noShow}</span>
            </div>
            <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mt-1">
              No-show rate: {total > 0 ? ((data.noShow / (total + data.canceled + data.noShow)) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusFunnel;
